import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, BarChart2, AlertTriangle, Database, Eye, TrendingUp } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import EmptyState from '@/components/ui/EmptyState';
import ChartCard from '@/components/ui/ChartCard';
import { format } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DataPoint {
  id: string;
  value: number;
  runDate: string;
  runId?: string;
  analyst?: string;
  notes?: string;
  violation?: string | null;
}

interface QCChart {
  id: string;
  chartCode: string;
  parameter: string;
  unit: string;
  lab: string;
  instrument?: string;
  method?: string;
  mean: number;
  sd: number;
  ucl: number;
  lcl: number;
  uwl: number;
  lwl: number;
  lastRunDate?: string;
  recentViolation?: string | null;
  dataPoints?: DataPoint[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_CHARTS: QCChart[] = [
  {
    id: '1', chartCode: 'QCC-001', parameter: 'Assay (Amoxicillin)', unit: '%',
    lab: 'Forge QC Central — Mumbai', instrument: 'HPLC-1', method: 'USP-AMX',
    mean: 100, sd: 1.5, ucl: 104.5, lcl: 95.5, uwl: 103, lwl: 97,
    lastRunDate: '2024-03-29T10:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'd1', value: 100.2, runDate: '2024-01-06T09:00:00Z', runId: 'RUN-001', analyst: 'Priya S.', violation: null },
      { id: 'd2', value: 99.8, runDate: '2024-01-13T09:00:00Z', runId: 'RUN-002', analyst: 'Priya S.', violation: null },
      { id: 'd3', value: 101.3, runDate: '2024-01-20T09:00:00Z', runId: 'RUN-003', analyst: 'Anil V.', violation: null },
      { id: 'd4', value: 98.5, runDate: '2024-01-27T09:00:00Z', runId: 'RUN-004', analyst: 'Anil V.', violation: null },
      { id: 'd5', value: 102.1, runDate: '2024-02-03T09:00:00Z', runId: 'RUN-005', analyst: 'Priya S.', violation: null },
      { id: 'd6', value: 100.7, runDate: '2024-02-10T09:00:00Z', runId: 'RUN-006', analyst: 'Meena P.', violation: null },
      { id: 'd7', value: 103.4, runDate: '2024-02-17T09:00:00Z', runId: 'RUN-007', analyst: 'Meena P.', violation: null },
      { id: 'd8', value: 104.8, runDate: '2024-02-24T09:00:00Z', runId: 'RUN-008', analyst: 'Meena P.', violation: '13s — Value exceeds UCL' },
      { id: 'd9', value: 100.1, runDate: '2024-03-02T09:00:00Z', runId: 'RUN-009', analyst: 'Priya S.', violation: null },
      { id: 'd10', value: 99.3, runDate: '2024-03-09T09:00:00Z', runId: 'RUN-010', analyst: 'Anil V.', violation: null },
      { id: 'd11', value: 101.5, runDate: '2024-03-16T09:00:00Z', runId: 'RUN-011', analyst: 'Sonal D.', violation: null },
      { id: 'd12', value: 100.8, runDate: '2024-03-23T09:00:00Z', runId: 'RUN-012', analyst: 'Deepak R.', violation: null },
      { id: 'd13', value: 99.6, runDate: '2024-03-29T09:00:00Z', runId: 'RUN-013', analyst: 'Priya S.', violation: null },
      { id: 'd14', value: 98.9, runDate: '2024-04-05T09:00:00Z', runId: 'RUN-014', analyst: 'Anil V.', violation: null },
      { id: 'd15', value: 103.2, runDate: '2024-04-12T09:00:00Z', runId: 'RUN-015', analyst: 'Meena P.', violation: null },
      { id: 'd16', value: 103.6, runDate: '2024-04-19T09:00:00Z', runId: 'RUN-016', analyst: 'Meena P.', violation: '22s — Two points beyond +2SD' },
      { id: 'd17', value: 101.1, runDate: '2024-04-26T09:00:00Z', runId: 'RUN-017', analyst: 'Priya S.', violation: null },
      { id: 'd18', value: 100.4, runDate: '2024-05-03T09:00:00Z', runId: 'RUN-018', analyst: 'Sonal D.', violation: null },
      { id: 'd19', value: 99.7, runDate: '2024-05-10T09:00:00Z', runId: 'RUN-019', analyst: 'Deepak R.', violation: null },
      { id: 'd20', value: 101.9, runDate: '2024-05-17T09:00:00Z', runId: 'RUN-020', analyst: 'Anil V.', violation: null },
      { id: 'd21', value: 100.3, runDate: '2024-05-24T09:00:00Z', runId: 'RUN-021', analyst: 'Priya S.', violation: null },
      { id: 'd22', value: 99.1, runDate: '2024-05-31T09:00:00Z', runId: 'RUN-022', analyst: 'Sonal D.', violation: null },
      { id: 'd23', value: 102.4, runDate: '2024-06-07T09:00:00Z', runId: 'RUN-023', analyst: 'Anil V.', violation: null },
      { id: 'd24', value: 100.6, runDate: '2024-06-14T09:00:00Z', runId: 'RUN-024', analyst: 'Meena P.', violation: null },
      { id: 'd25', value: 99.9, runDate: '2024-06-21T09:00:00Z', runId: 'RUN-025', analyst: 'Priya S.', violation: null },
      { id: 'd26', value: 100.0, runDate: '2024-06-28T09:00:00Z', runId: 'RUN-026', analyst: 'Deepak R.', violation: null },
      { id: 'd27', value: 101.2, runDate: '2024-07-05T09:00:00Z', runId: 'RUN-027', analyst: 'Sonal D.', violation: null },
      { id: 'd28', value: 99.5, runDate: '2024-07-12T09:00:00Z', runId: 'RUN-028', analyst: 'Priya S.', violation: null },
    ],
  },
  {
    id: '2', chartCode: 'QCC-002', parameter: 'Dissolution (Paracetamol)', unit: '%',
    lab: 'Forge QC Central — Mumbai', instrument: 'Dissolution Apparatus 1', method: 'USP Apparatus I',
    mean: 85, sd: 2.0, ucl: 91, lcl: 79, uwl: 89, lwl: 81,
    lastRunDate: '2024-07-14T11:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'd29', value: 85.5, runDate: '2024-01-07T11:00:00Z', runId: 'RUN-001', analyst: 'Anil V.', violation: null },
      { id: 'd30', value: 87.2, runDate: '2024-01-14T11:00:00Z', runId: 'RUN-002', analyst: 'Anil V.', violation: null },
      { id: 'd31', value: 83.8, runDate: '2024-01-21T11:00:00Z', runId: 'RUN-003', analyst: 'Priya S.', violation: null },
      { id: 'd32', value: 86.1, runDate: '2024-01-28T11:00:00Z', runId: 'RUN-004', analyst: 'Meena P.', violation: null },
      { id: 'd33', value: 89.5, runDate: '2024-02-04T11:00:00Z', runId: 'RUN-005', analyst: 'Priya S.', violation: '22s — Two consecutive points beyond 2SD' },
      { id: 'd34', value: 90.1, runDate: '2024-02-11T11:00:00Z', runId: 'RUN-006', analyst: 'Priya S.', violation: '22s — Two consecutive points beyond 2SD' },
      { id: 'd35', value: 85.0, runDate: '2024-02-18T11:00:00Z', runId: 'RUN-007', analyst: 'Meena P.', violation: null },
      { id: 'd36', value: 84.3, runDate: '2024-02-25T11:00:00Z', runId: 'RUN-008', analyst: 'Deepak R.', violation: null },
      { id: 'd37', value: 86.7, runDate: '2024-03-03T11:00:00Z', runId: 'RUN-009', analyst: 'Anil V.', violation: null },
      { id: 'd38', value: 85.9, runDate: '2024-03-10T11:00:00Z', runId: 'RUN-010', analyst: 'Sonal D.', violation: null },
      { id: 'd39', value: 83.4, runDate: '2024-03-17T11:00:00Z', runId: 'RUN-011', analyst: 'Priya S.', violation: null },
      { id: 'd40', value: 86.2, runDate: '2024-03-24T11:00:00Z', runId: 'RUN-012', analyst: 'Anil V.', violation: null },
      { id: 'd41', value: 87.8, runDate: '2024-03-31T11:00:00Z', runId: 'RUN-013', analyst: 'Meena P.', violation: null },
      { id: 'd42', value: 85.1, runDate: '2024-04-07T11:00:00Z', runId: 'RUN-014', analyst: 'Deepak R.', violation: null },
      { id: 'd43', value: 84.6, runDate: '2024-04-14T11:00:00Z', runId: 'RUN-015', analyst: 'Sonal D.', violation: null },
      { id: 'd44', value: 86.3, runDate: '2024-04-21T11:00:00Z', runId: 'RUN-016', analyst: 'Anil V.', violation: null },
      { id: 'd45', value: 91.4, runDate: '2024-04-28T11:00:00Z', runId: 'RUN-017', analyst: 'Priya S.', violation: '13s — Value exceeds UCL' },
      { id: 'd46', value: 85.5, runDate: '2024-05-05T11:00:00Z', runId: 'RUN-018', analyst: 'Meena P.', violation: null },
      { id: 'd47', value: 84.9, runDate: '2024-05-12T11:00:00Z', runId: 'RUN-019', analyst: 'Deepak R.', violation: null },
      { id: 'd48', value: 86.0, runDate: '2024-05-19T11:00:00Z', runId: 'RUN-020', analyst: 'Anil V.', violation: null },
      { id: 'd49', value: 85.3, runDate: '2024-05-26T11:00:00Z', runId: 'RUN-021', analyst: 'Sonal D.', violation: null },
      { id: 'd50', value: 84.7, runDate: '2024-06-02T11:00:00Z', runId: 'RUN-022', analyst: 'Priya S.', violation: null },
      { id: 'd51', value: 87.1, runDate: '2024-06-09T11:00:00Z', runId: 'RUN-023', analyst: 'Meena P.', violation: null },
      { id: 'd52', value: 85.8, runDate: '2024-06-16T11:00:00Z', runId: 'RUN-024', analyst: 'Anil V.', violation: null },
      { id: 'd53', value: 84.2, runDate: '2024-06-23T11:00:00Z', runId: 'RUN-025', analyst: 'Sonal D.', violation: null },
      { id: 'd54', value: 86.5, runDate: '2024-06-30T11:00:00Z', runId: 'RUN-026', analyst: 'Deepak R.', violation: null },
      { id: 'd55', value: 85.0, runDate: '2024-07-07T11:00:00Z', runId: 'RUN-027', analyst: 'Priya S.', violation: null },
      { id: 'd56', value: 85.7, runDate: '2024-07-14T11:00:00Z', runId: 'RUN-028', analyst: 'Anil V.', violation: null },
    ],
  },
  {
    id: '3', chartCode: 'QCC-003', parameter: 'Water Content (Metformin)', unit: '%',
    lab: 'Forge R&D Lab — Pune', instrument: 'Karl Fischer', method: 'USP KF',
    mean: 3.0, sd: 0.3, ucl: 3.9, lcl: 2.1, uwl: 3.6, lwl: 2.4,
    lastRunDate: '2024-07-13T14:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'd57', value: 3.1, runDate: '2024-01-06T14:00:00Z', runId: 'RUN-001', analyst: 'Deepak R.', violation: null },
      { id: 'd58', value: 2.9, runDate: '2024-01-13T14:00:00Z', runId: 'RUN-002', analyst: 'Deepak R.', violation: null },
      { id: 'd59', value: 3.0, runDate: '2024-01-20T14:00:00Z', runId: 'RUN-003', analyst: 'Sonal D.', violation: null },
      { id: 'd60', value: 3.2, runDate: '2024-01-27T14:00:00Z', runId: 'RUN-004', analyst: 'Deepak R.', violation: null },
      { id: 'd61', value: 2.8, runDate: '2024-02-03T14:00:00Z', runId: 'RUN-005', analyst: 'Meena P.', violation: null },
      { id: 'd62', value: 3.3, runDate: '2024-02-10T14:00:00Z', runId: 'RUN-006', analyst: 'Sonal D.', violation: null },
      { id: 'd63', value: 3.6, runDate: '2024-02-17T14:00:00Z', runId: 'RUN-007', analyst: 'Deepak R.', violation: null },
      { id: 'd64', value: 3.7, runDate: '2024-02-24T14:00:00Z', runId: 'RUN-008', analyst: 'Deepak R.', violation: '22s — Two consecutive points beyond +2SD' },
      { id: 'd65', value: 3.0, runDate: '2024-03-02T14:00:00Z', runId: 'RUN-009', analyst: 'Meena P.', violation: null },
      { id: 'd66', value: 2.9, runDate: '2024-03-09T14:00:00Z', runId: 'RUN-010', analyst: 'Sonal D.', violation: null },
      { id: 'd67', value: 3.1, runDate: '2024-03-16T14:00:00Z', runId: 'RUN-011', analyst: 'Deepak R.', violation: null },
      { id: 'd68', value: 3.4, runDate: '2024-03-23T14:00:00Z', runId: 'RUN-012', analyst: 'Deepak R.', violation: null },
      { id: 'd69', value: 2.7, runDate: '2024-03-30T14:00:00Z', runId: 'RUN-013', analyst: 'Meena P.', violation: null },
      { id: 'd70', value: 3.0, runDate: '2024-04-06T14:00:00Z', runId: 'RUN-014', analyst: 'Sonal D.', violation: null },
      { id: 'd71', value: 3.2, runDate: '2024-04-13T14:00:00Z', runId: 'RUN-015', analyst: 'Deepak R.', violation: null },
      { id: 'd72', value: 2.8, runDate: '2024-04-20T14:00:00Z', runId: 'RUN-016', analyst: 'Meena P.', violation: null },
      { id: 'd73', value: 3.9, runDate: '2024-04-27T14:00:00Z', runId: 'RUN-017', analyst: 'Sonal D.', violation: '13s — Value at UCL boundary' },
      { id: 'd74', value: 3.1, runDate: '2024-05-04T14:00:00Z', runId: 'RUN-018', analyst: 'Deepak R.', violation: null },
      { id: 'd75', value: 3.0, runDate: '2024-05-11T14:00:00Z', runId: 'RUN-019', analyst: 'Sonal D.', violation: null },
      { id: 'd76', value: 2.9, runDate: '2024-05-18T14:00:00Z', runId: 'RUN-020', analyst: 'Deepak R.', violation: null },
      { id: 'd77', value: 3.3, runDate: '2024-05-25T14:00:00Z', runId: 'RUN-021', analyst: 'Meena P.', violation: null },
      { id: 'd78', value: 2.8, runDate: '2024-06-01T14:00:00Z', runId: 'RUN-022', analyst: 'Sonal D.', violation: null },
      { id: 'd79', value: 3.1, runDate: '2024-06-08T14:00:00Z', runId: 'RUN-023', analyst: 'Deepak R.', violation: null },
      { id: 'd80', value: 3.0, runDate: '2024-06-15T14:00:00Z', runId: 'RUN-024', analyst: 'Meena P.', violation: null },
      { id: 'd81', value: 3.2, runDate: '2024-06-22T14:00:00Z', runId: 'RUN-025', analyst: 'Sonal D.', violation: null },
      { id: 'd82', value: 2.9, runDate: '2024-06-29T14:00:00Z', runId: 'RUN-026', analyst: 'Deepak R.', violation: null },
      { id: 'd83', value: 3.0, runDate: '2024-07-06T14:00:00Z', runId: 'RUN-027', analyst: 'Meena P.', violation: null },
      { id: 'd84', value: 3.1, runDate: '2024-07-13T14:00:00Z', runId: 'RUN-028', analyst: 'Sonal D.', violation: null },
    ],
  },
  // ── 5 additional charts ────────────────────────────────────────────────────
  {
    id: '4', chartCode: 'QCC-004', parameter: 'Related Substances (Ciprofloxacin)', unit: '%',
    lab: 'Forge API Lab — Hyderabad', instrument: 'HPLC-2', method: 'IP 2023 Method',
    mean: 0.2, sd: 0.05, ucl: 0.35, lcl: 0.05, uwl: 0.30, lwl: 0.10,
    lastRunDate: '2024-07-12T10:30:00Z', recentViolation: null,
    dataPoints: [
      { id: 'e1', value: 0.19, runDate: '2024-01-06T10:30:00Z', runId: 'RUN-001', analyst: 'Priya S.', violation: null },
      { id: 'e2', value: 0.21, runDate: '2024-01-13T10:30:00Z', runId: 'RUN-002', analyst: 'Anil V.', violation: null },
      { id: 'e3', value: 0.18, runDate: '2024-01-20T10:30:00Z', runId: 'RUN-003', analyst: 'Priya S.', violation: null },
      { id: 'e4', value: 0.22, runDate: '2024-01-27T10:30:00Z', runId: 'RUN-004', analyst: 'Meena P.', violation: null },
      { id: 'e5', value: 0.30, runDate: '2024-02-03T10:30:00Z', runId: 'RUN-005', analyst: 'Priya S.', violation: null },
      { id: 'e6', value: 0.31, runDate: '2024-02-10T10:30:00Z', runId: 'RUN-006', analyst: 'Priya S.', violation: '22s — Two consecutive points beyond +2SD' },
      { id: 'e7', value: 0.20, runDate: '2024-02-17T10:30:00Z', runId: 'RUN-007', analyst: 'Anil V.', violation: null },
      { id: 'e8', value: 0.19, runDate: '2024-02-24T10:30:00Z', runId: 'RUN-008', analyst: 'Sonal D.', violation: null },
      { id: 'e9', value: 0.21, runDate: '2024-03-02T10:30:00Z', runId: 'RUN-009', analyst: 'Deepak R.', violation: null },
      { id: 'e10', value: 0.18, runDate: '2024-03-09T10:30:00Z', runId: 'RUN-010', analyst: 'Priya S.', violation: null },
      { id: 'e11', value: 0.20, runDate: '2024-03-16T10:30:00Z', runId: 'RUN-011', analyst: 'Anil V.', violation: null },
      { id: 'e12', value: 0.36, runDate: '2024-03-23T10:30:00Z', runId: 'RUN-012', analyst: 'Meena P.', violation: '13s — Value exceeds UCL' },
      { id: 'e13', value: 0.21, runDate: '2024-03-30T10:30:00Z', runId: 'RUN-013', analyst: 'Sonal D.', violation: null },
      { id: 'e14', value: 0.19, runDate: '2024-04-06T10:30:00Z', runId: 'RUN-014', analyst: 'Deepak R.', violation: null },
      { id: 'e15', value: 0.22, runDate: '2024-04-13T10:30:00Z', runId: 'RUN-015', analyst: 'Priya S.', violation: null },
      { id: 'e16', value: 0.20, runDate: '2024-04-20T10:30:00Z', runId: 'RUN-016', analyst: 'Anil V.', violation: null },
      { id: 'e17', value: 0.18, runDate: '2024-04-27T10:30:00Z', runId: 'RUN-017', analyst: 'Meena P.', violation: null },
      { id: 'e18', value: 0.21, runDate: '2024-05-04T10:30:00Z', runId: 'RUN-018', analyst: 'Sonal D.', violation: null },
      { id: 'e19', value: 0.19, runDate: '2024-05-11T10:30:00Z', runId: 'RUN-019', analyst: 'Deepak R.', violation: null },
      { id: 'e20', value: 0.23, runDate: '2024-05-18T10:30:00Z', runId: 'RUN-020', analyst: 'Priya S.', violation: null },
      { id: 'e21', value: 0.20, runDate: '2024-05-25T10:30:00Z', runId: 'RUN-021', analyst: 'Anil V.', violation: null },
      { id: 'e22', value: 0.17, runDate: '2024-06-01T10:30:00Z', runId: 'RUN-022', analyst: 'Sonal D.', violation: null },
      { id: 'e23', value: 0.21, runDate: '2024-06-08T10:30:00Z', runId: 'RUN-023', analyst: 'Deepak R.', violation: null },
      { id: 'e24', value: 0.20, runDate: '2024-06-15T10:30:00Z', runId: 'RUN-024', analyst: 'Priya S.', violation: null },
      { id: 'e25', value: 0.22, runDate: '2024-06-22T10:30:00Z', runId: 'RUN-025', analyst: 'Meena P.', violation: null },
      { id: 'e26', value: 0.19, runDate: '2024-06-29T10:30:00Z', runId: 'RUN-026', analyst: 'Anil V.', violation: null },
      { id: 'e27', value: 0.20, runDate: '2024-07-06T10:30:00Z', runId: 'RUN-027', analyst: 'Sonal D.', violation: null },
      { id: 'e28', value: 0.21, runDate: '2024-07-12T10:30:00Z', runId: 'RUN-028', analyst: 'Deepak R.', violation: null },
    ],
  },
  {
    id: '5', chartCode: 'QCC-005', parameter: 'Assay (Atorvastatin)', unit: '%',
    lab: 'Forge API Lab — Hyderabad', instrument: 'HPLC-3', method: 'IP 2023 Atorvastatin',
    mean: 100, sd: 1.2, ucl: 103.6, lcl: 96.4, uwl: 102.4, lwl: 97.6,
    lastRunDate: '2024-07-11T09:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'f1', value: 100.1, runDate: '2024-01-05T09:00:00Z', runId: 'RUN-001', analyst: 'Deepak R.', violation: null },
      { id: 'f2', value: 99.4, runDate: '2024-01-12T09:00:00Z', runId: 'RUN-002', analyst: 'Sonal D.', violation: null },
      { id: 'f3', value: 101.2, runDate: '2024-01-19T09:00:00Z', runId: 'RUN-003', analyst: 'Anil V.', violation: null },
      { id: 'f4', value: 100.7, runDate: '2024-01-26T09:00:00Z', runId: 'RUN-004', analyst: 'Priya S.', violation: null },
      { id: 'f5', value: 99.8, runDate: '2024-02-02T09:00:00Z', runId: 'RUN-005', analyst: 'Deepak R.', violation: null },
      { id: 'f6', value: 102.5, runDate: '2024-02-09T09:00:00Z', runId: 'RUN-006', analyst: 'Sonal D.', violation: null },
      { id: 'f7', value: 102.6, runDate: '2024-02-16T09:00:00Z', runId: 'RUN-007', analyst: 'Anil V.', violation: '22s — Two consecutive points beyond +2SD' },
      { id: 'f8', value: 100.3, runDate: '2024-02-23T09:00:00Z', runId: 'RUN-008', analyst: 'Meena P.', violation: null },
      { id: 'f9', value: 99.5, runDate: '2024-03-01T09:00:00Z', runId: 'RUN-009', analyst: 'Priya S.', violation: null },
      { id: 'f10', value: 101.4, runDate: '2024-03-08T09:00:00Z', runId: 'RUN-010', analyst: 'Deepak R.', violation: null },
      { id: 'f11', value: 100.9, runDate: '2024-03-15T09:00:00Z', runId: 'RUN-011', analyst: 'Sonal D.', violation: null },
      { id: 'f12', value: 99.6, runDate: '2024-03-22T09:00:00Z', runId: 'RUN-012', analyst: 'Anil V.', violation: null },
      { id: 'f13', value: 100.0, runDate: '2024-03-29T09:00:00Z', runId: 'RUN-013', analyst: 'Meena P.', violation: null },
      { id: 'f14', value: 103.7, runDate: '2024-04-05T09:00:00Z', runId: 'RUN-014', analyst: 'Priya S.', violation: '13s — Value exceeds UCL' },
      { id: 'f15', value: 100.2, runDate: '2024-04-12T09:00:00Z', runId: 'RUN-015', analyst: 'Deepak R.', violation: null },
      { id: 'f16', value: 99.7, runDate: '2024-04-19T09:00:00Z', runId: 'RUN-016', analyst: 'Sonal D.', violation: null },
      { id: 'f17', value: 101.0, runDate: '2024-04-26T09:00:00Z', runId: 'RUN-017', analyst: 'Anil V.', violation: null },
      { id: 'f18', value: 100.5, runDate: '2024-05-03T09:00:00Z', runId: 'RUN-018', analyst: 'Priya S.', violation: null },
      { id: 'f19', value: 98.9, runDate: '2024-05-10T09:00:00Z', runId: 'RUN-019', analyst: 'Meena P.', violation: null },
      { id: 'f20', value: 100.8, runDate: '2024-05-17T09:00:00Z', runId: 'RUN-020', analyst: 'Deepak R.', violation: null },
      { id: 'f21', value: 99.3, runDate: '2024-05-24T09:00:00Z', runId: 'RUN-021', analyst: 'Sonal D.', violation: null },
      { id: 'f22', value: 101.6, runDate: '2024-05-31T09:00:00Z', runId: 'RUN-022', analyst: 'Anil V.', violation: null },
      { id: 'f23', value: 100.4, runDate: '2024-06-07T09:00:00Z', runId: 'RUN-023', analyst: 'Priya S.', violation: null },
      { id: 'f24', value: 99.8, runDate: '2024-06-14T09:00:00Z', runId: 'RUN-024', analyst: 'Meena P.', violation: null },
      { id: 'f25', value: 100.1, runDate: '2024-06-21T09:00:00Z', runId: 'RUN-025', analyst: 'Deepak R.', violation: null },
      { id: 'f26', value: 101.3, runDate: '2024-06-28T09:00:00Z', runId: 'RUN-026', analyst: 'Sonal D.', violation: null },
      { id: 'f27', value: 99.9, runDate: '2024-07-05T09:00:00Z', runId: 'RUN-027', analyst: 'Anil V.', violation: null },
      { id: 'f28', value: 100.2, runDate: '2024-07-11T09:00:00Z', runId: 'RUN-028', analyst: 'Priya S.', violation: null },
    ],
  },
  {
    id: '6', chartCode: 'QCC-006', parameter: 'pH (Saline Injection)', unit: 'pH',
    lab: 'Forge Sterile Lab — Chennai', instrument: 'pH Meter', method: 'USP pH Method',
    mean: 7.0, sd: 0.08, ucl: 7.24, lcl: 6.76, uwl: 7.16, lwl: 6.84,
    lastRunDate: '2024-07-13T08:00:00Z', recentViolation: '41s — Four consecutive points >1SD',
    dataPoints: [
      { id: 'g1', value: 7.01, runDate: '2024-01-06T08:00:00Z', runId: 'RUN-001', analyst: 'Sonal D.', violation: null },
      { id: 'g2', value: 6.98, runDate: '2024-01-13T08:00:00Z', runId: 'RUN-002', analyst: 'Deepak R.', violation: null },
      { id: 'g3', value: 7.03, runDate: '2024-01-20T08:00:00Z', runId: 'RUN-003', analyst: 'Sonal D.', violation: null },
      { id: 'g4', value: 7.00, runDate: '2024-01-27T08:00:00Z', runId: 'RUN-004', analyst: 'Meena P.', violation: null },
      { id: 'g5', value: 6.97, runDate: '2024-02-03T08:00:00Z', runId: 'RUN-005', analyst: 'Anil V.', violation: null },
      { id: 'g6', value: 7.05, runDate: '2024-02-10T08:00:00Z', runId: 'RUN-006', analyst: 'Sonal D.', violation: null },
      { id: 'g7', value: 7.18, runDate: '2024-02-17T08:00:00Z', runId: 'RUN-007', analyst: 'Deepak R.', violation: null },
      { id: 'g8', value: 7.25, runDate: '2024-02-24T08:00:00Z', runId: 'RUN-008', analyst: 'Priya S.', violation: '13s — Value exceeds UCL' },
      { id: 'g9', value: 7.02, runDate: '2024-03-02T08:00:00Z', runId: 'RUN-009', analyst: 'Sonal D.', violation: null },
      { id: 'g10', value: 6.99, runDate: '2024-03-09T08:00:00Z', runId: 'RUN-010', analyst: 'Meena P.', violation: null },
      { id: 'g11', value: 7.04, runDate: '2024-03-16T08:00:00Z', runId: 'RUN-011', analyst: 'Anil V.', violation: null },
      { id: 'g12', value: 7.01, runDate: '2024-03-23T08:00:00Z', runId: 'RUN-012', analyst: 'Deepak R.', violation: null },
      { id: 'g13', value: 6.96, runDate: '2024-03-30T08:00:00Z', runId: 'RUN-013', analyst: 'Sonal D.', violation: null },
      { id: 'g14', value: 7.03, runDate: '2024-04-06T08:00:00Z', runId: 'RUN-014', analyst: 'Priya S.', violation: null },
      { id: 'g15', value: 7.00, runDate: '2024-04-13T08:00:00Z', runId: 'RUN-015', analyst: 'Meena P.', violation: null },
      { id: 'g16', value: 7.09, runDate: '2024-04-20T08:00:00Z', runId: 'RUN-016', analyst: 'Sonal D.', violation: null },
      { id: 'g17', value: 7.10, runDate: '2024-04-27T08:00:00Z', runId: 'RUN-017', analyst: 'Deepak R.', violation: null },
      { id: 'g18', value: 7.11, runDate: '2024-05-04T08:00:00Z', runId: 'RUN-018', analyst: 'Anil V.', violation: null },
      { id: 'g19', value: 7.09, runDate: '2024-05-11T08:00:00Z', runId: 'RUN-019', analyst: 'Sonal D.', violation: '41s — Four consecutive points >1SD' },
      { id: 'g20', value: 7.02, runDate: '2024-05-18T08:00:00Z', runId: 'RUN-020', analyst: 'Priya S.', violation: null },
      { id: 'g21', value: 6.99, runDate: '2024-05-25T08:00:00Z', runId: 'RUN-021', analyst: 'Meena P.', violation: null },
      { id: 'g22', value: 7.01, runDate: '2024-06-01T08:00:00Z', runId: 'RUN-022', analyst: 'Sonal D.', violation: null },
      { id: 'g23', value: 7.00, runDate: '2024-06-08T08:00:00Z', runId: 'RUN-023', analyst: 'Deepak R.', violation: null },
      { id: 'g24', value: 6.98, runDate: '2024-06-15T08:00:00Z', runId: 'RUN-024', analyst: 'Anil V.', violation: null },
      { id: 'g25', value: 7.03, runDate: '2024-06-22T08:00:00Z', runId: 'RUN-025', analyst: 'Sonal D.', violation: null },
      { id: 'g26', value: 7.05, runDate: '2024-06-29T08:00:00Z', runId: 'RUN-026', analyst: 'Priya S.', violation: null },
      { id: 'g27', value: 6.97, runDate: '2024-07-06T08:00:00Z', runId: 'RUN-027', analyst: 'Meena P.', violation: null },
      { id: 'g28', value: 7.02, runDate: '2024-07-13T08:00:00Z', runId: 'RUN-028', analyst: 'Sonal D.', violation: null },
    ],
  },
  {
    id: '7', chartCode: 'QCC-007', parameter: 'Endotoxin (Sterile Products)', unit: 'EU/mL',
    lab: 'Forge Sterile Lab — Chennai', instrument: 'LAL Reader', method: 'USP <85> LAL',
    mean: 0.15, sd: 0.02, ucl: 0.21, lcl: 0.09, uwl: 0.19, lwl: 0.11,
    lastRunDate: '2024-07-12T13:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'h1', value: 0.15, runDate: '2024-01-06T13:00:00Z', runId: 'RUN-001', analyst: 'Meena P.', violation: null },
      { id: 'h2', value: 0.14, runDate: '2024-01-13T13:00:00Z', runId: 'RUN-002', analyst: 'Priya S.', violation: null },
      { id: 'h3', value: 0.16, runDate: '2024-01-20T13:00:00Z', runId: 'RUN-003', analyst: 'Deepak R.', violation: null },
      { id: 'h4', value: 0.15, runDate: '2024-01-27T13:00:00Z', runId: 'RUN-004', analyst: 'Meena P.', violation: null },
      { id: 'h5', value: 0.13, runDate: '2024-02-03T13:00:00Z', runId: 'RUN-005', analyst: 'Anil V.', violation: null },
      { id: 'h6', value: 0.17, runDate: '2024-02-10T13:00:00Z', runId: 'RUN-006', analyst: 'Priya S.', violation: null },
      { id: 'h7', value: 0.19, runDate: '2024-02-17T13:00:00Z', runId: 'RUN-007', analyst: 'Sonal D.', violation: null },
      { id: 'h8', value: 0.20, runDate: '2024-02-24T13:00:00Z', runId: 'RUN-008', analyst: 'Meena P.', violation: '22s — Two consecutive points beyond +2SD' },
      { id: 'h9', value: 0.15, runDate: '2024-03-02T13:00:00Z', runId: 'RUN-009', analyst: 'Deepak R.', violation: null },
      { id: 'h10', value: 0.14, runDate: '2024-03-09T13:00:00Z', runId: 'RUN-010', analyst: 'Anil V.', violation: null },
      { id: 'h11', value: 0.16, runDate: '2024-03-16T13:00:00Z', runId: 'RUN-011', analyst: 'Priya S.', violation: null },
      { id: 'h12', value: 0.15, runDate: '2024-03-23T13:00:00Z', runId: 'RUN-012', analyst: 'Sonal D.', violation: null },
      { id: 'h13', value: 0.22, runDate: '2024-03-30T13:00:00Z', runId: 'RUN-013', analyst: 'Meena P.', violation: '13s — Value exceeds UCL' },
      { id: 'h14', value: 0.15, runDate: '2024-04-06T13:00:00Z', runId: 'RUN-014', analyst: 'Deepak R.', violation: null },
      { id: 'h15', value: 0.14, runDate: '2024-04-13T13:00:00Z', runId: 'RUN-015', analyst: 'Anil V.', violation: null },
      { id: 'h16', value: 0.16, runDate: '2024-04-20T13:00:00Z', runId: 'RUN-016', analyst: 'Priya S.', violation: null },
      { id: 'h17', value: 0.13, runDate: '2024-04-27T13:00:00Z', runId: 'RUN-017', analyst: 'Sonal D.', violation: null },
      { id: 'h18', value: 0.15, runDate: '2024-05-04T13:00:00Z', runId: 'RUN-018', analyst: 'Meena P.', violation: null },
      { id: 'h19', value: 0.17, runDate: '2024-05-11T13:00:00Z', runId: 'RUN-019', analyst: 'Deepak R.', violation: null },
      { id: 'h20', value: 0.14, runDate: '2024-05-18T13:00:00Z', runId: 'RUN-020', analyst: 'Anil V.', violation: null },
      { id: 'h21', value: 0.16, runDate: '2024-05-25T13:00:00Z', runId: 'RUN-021', analyst: 'Priya S.', violation: null },
      { id: 'h22', value: 0.15, runDate: '2024-06-01T13:00:00Z', runId: 'RUN-022', analyst: 'Sonal D.', violation: null },
      { id: 'h23', value: 0.14, runDate: '2024-06-08T13:00:00Z', runId: 'RUN-023', analyst: 'Deepak R.', violation: null },
      { id: 'h24', value: 0.15, runDate: '2024-06-15T13:00:00Z', runId: 'RUN-024', analyst: 'Meena P.', violation: null },
      { id: 'h25', value: 0.16, runDate: '2024-06-22T13:00:00Z', runId: 'RUN-025', analyst: 'Anil V.', violation: null },
      { id: 'h26', value: 0.13, runDate: '2024-06-29T13:00:00Z', runId: 'RUN-026', analyst: 'Priya S.', violation: null },
      { id: 'h27', value: 0.15, runDate: '2024-07-06T13:00:00Z', runId: 'RUN-027', analyst: 'Sonal D.', violation: null },
      { id: 'h28', value: 0.14, runDate: '2024-07-12T13:00:00Z', runId: 'RUN-028', analyst: 'Deepak R.', violation: null },
    ],
  },
  {
    id: '8', chartCode: 'QCC-008', parameter: 'Particle Count (Injectable)', unit: 'particles/mL',
    lab: 'Forge Sterile Lab — Chennai', instrument: 'Light Obscuration', method: 'USP <788>',
    mean: 120, sd: 15, ucl: 165, lcl: 75, uwl: 150, lwl: 90,
    lastRunDate: '2024-07-11T15:00:00Z', recentViolation: null,
    dataPoints: [
      { id: 'i1', value: 118, runDate: '2024-01-06T15:00:00Z', runId: 'RUN-001', analyst: 'Priya S.', violation: null },
      { id: 'i2', value: 125, runDate: '2024-01-13T15:00:00Z', runId: 'RUN-002', analyst: 'Anil V.', violation: null },
      { id: 'i3', value: 110, runDate: '2024-01-20T15:00:00Z', runId: 'RUN-003', analyst: 'Sonal D.', violation: null },
      { id: 'i4', value: 130, runDate: '2024-01-27T15:00:00Z', runId: 'RUN-004', analyst: 'Deepak R.', violation: null },
      { id: 'i5', value: 122, runDate: '2024-02-03T15:00:00Z', runId: 'RUN-005', analyst: 'Meena P.', violation: null },
      { id: 'i6', value: 115, runDate: '2024-02-10T15:00:00Z', runId: 'RUN-006', analyst: 'Priya S.', violation: null },
      { id: 'i7', value: 151, runDate: '2024-02-17T15:00:00Z', runId: 'RUN-007', analyst: 'Anil V.', violation: null },
      { id: 'i8', value: 153, runDate: '2024-02-24T15:00:00Z', runId: 'RUN-008', analyst: 'Sonal D.', violation: '22s — Two consecutive points beyond +2SD' },
      { id: 'i9', value: 119, runDate: '2024-03-02T15:00:00Z', runId: 'RUN-009', analyst: 'Deepak R.', violation: null },
      { id: 'i10', value: 124, runDate: '2024-03-09T15:00:00Z', runId: 'RUN-010', analyst: 'Priya S.', violation: null },
      { id: 'i11', value: 112, runDate: '2024-03-16T15:00:00Z', runId: 'RUN-011', analyst: 'Meena P.', violation: null },
      { id: 'i12', value: 128, runDate: '2024-03-23T15:00:00Z', runId: 'RUN-012', analyst: 'Anil V.', violation: null },
      { id: 'i13', value: 121, runDate: '2024-03-30T15:00:00Z', runId: 'RUN-013', analyst: 'Sonal D.', violation: null },
      { id: 'i14', value: 167, runDate: '2024-04-06T15:00:00Z', runId: 'RUN-014', analyst: 'Deepak R.', violation: '13s — Value exceeds UCL' },
      { id: 'i15', value: 120, runDate: '2024-04-13T15:00:00Z', runId: 'RUN-015', analyst: 'Priya S.', violation: null },
      { id: 'i16', value: 116, runDate: '2024-04-20T15:00:00Z', runId: 'RUN-016', analyst: 'Meena P.', violation: null },
      { id: 'i17', value: 127, runDate: '2024-04-27T15:00:00Z', runId: 'RUN-017', analyst: 'Anil V.', violation: null },
      { id: 'i18', value: 113, runDate: '2024-05-04T15:00:00Z', runId: 'RUN-018', analyst: 'Sonal D.', violation: null },
      { id: 'i19', value: 122, runDate: '2024-05-11T15:00:00Z', runId: 'RUN-019', analyst: 'Deepak R.', violation: null },
      { id: 'i20', value: 118, runDate: '2024-05-18T15:00:00Z', runId: 'RUN-020', analyst: 'Priya S.', violation: null },
      { id: 'i21', value: 131, runDate: '2024-05-25T15:00:00Z', runId: 'RUN-021', analyst: 'Meena P.', violation: null },
      { id: 'i22', value: 120, runDate: '2024-06-01T15:00:00Z', runId: 'RUN-022', analyst: 'Anil V.', violation: null },
      { id: 'i23', value: 107, runDate: '2024-06-08T15:00:00Z', runId: 'RUN-023', analyst: 'Sonal D.', violation: null },
      { id: 'i24', value: 125, runDate: '2024-06-15T15:00:00Z', runId: 'RUN-024', analyst: 'Deepak R.', violation: null },
      { id: 'i25', value: 119, runDate: '2024-06-22T15:00:00Z', runId: 'RUN-025', analyst: 'Priya S.', violation: null },
      { id: 'i26', value: 123, runDate: '2024-06-29T15:00:00Z', runId: 'RUN-026', analyst: 'Meena P.', violation: null },
      { id: 'i27', value: 115, runDate: '2024-07-06T15:00:00Z', runId: 'RUN-027', analyst: 'Anil V.', violation: null },
      { id: 'i28', value: 121, runDate: '2024-07-11T15:00:00Z', runId: 'RUN-028', analyst: 'Sonal D.', violation: null },
    ],
  },
];

// ─── Levey-Jennings Custom Dot ────────────────────────────────────────────────

interface LJDotProps {
  cx?: number;
  cy?: number;
  payload?: { violation?: string | null };
}

function LJDot(props: LJDotProps) {
  const { cx = 0, cy = 0, payload } = props;
  const isViolation = payload?.violation;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={isViolation ? 6 : 4}
      fill={isViolation ? '#DC2626' : '#16A34A'}
      stroke={isViolation ? '#991B1B' : '#15803D'}
      strokeWidth={isViolation ? 2 : 1}
    />
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, highlight }: { label: string; value: number; icon: React.ElementType; color: string; highlight?: boolean }) {
  return (
    <div className={`card p-4 flex items-center gap-4 ${highlight && value > 0 ? 'border border-red-200 bg-red-50/20' : ''}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className={`text-2xl font-bold font-serif ${highlight && value > 0 ? 'text-red-600' : 'text-text'}`}>{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function QCCharts() {
  const [search, setSearch] = useState('');
  const [labFilter, setLabFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedChart, setSelectedChart] = useState<QCChart | null>(null);
  const [showAddPoint, setShowAddPoint] = useState(false);

  const [createForm, setCreateForm] = useState({
    parameter: '', unit: '', labId: '', mean: '', sd: '', instrument: '', method: '',
  });

  const [pointForm, setPointForm] = useState({
    value: '', runDate: '', analyst: '', runId: '', notes: '',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['qc-charts', { search, lab: labFilter, page }],
    queryFn: () =>
      api.get('/qc-charts', { params: { search, lab: labFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_CHARTS, meta: { total: MOCK_CHARTS.length } })),
  });

  const { data: chartDetailData } = useQuery({
    queryKey: ['qc-chart-detail', selectedChart?.id],
    queryFn: () =>
      api.get(`/qc-charts/${selectedChart!.id}/data`)
        .then((r) => r.data)
        .catch(() => null),
    enabled: !!selectedChart,
  });

  const rows: QCChart[] = (Array.isArray(data?.data) && data.data.length > 0) ? data.data : MOCK_CHARTS;
  const total: number = data?.meta?.total ?? rows.length;

  // Use detail data or fall back to embedded mock data
  const activeDataPoints: DataPoint[] = Array.isArray(chartDetailData?.dataPoints)
    ? chartDetailData.dataPoints
    : Array.isArray(selectedChart?.dataPoints)
    ? selectedChart!.dataPoints
    : [];

  const today = new Date().toDateString();
  const violationsToday = rows.reduce((acc, c) => {
    if (!c.recentViolation) return acc;
    if (!c.lastRunDate) return acc;
    const isToday = new Date(c.lastRunDate).toDateString() === today;
    return acc + (isToday ? 1 : 0);
  }, 0);

  const stats = {
    active: rows.length,
    violationsToday,
    totalPoints: rows.reduce((acc, c) => acc + (Array.isArray(c.dataPoints) ? c.dataPoints.length : 0), 0),
    needAttention: rows.filter((c) => !!c.recentViolation).length,
  };

  // Auto-calculate control limits when mean/sd change in form
  const autoCalcMean = Number(createForm.mean) || 0;
  const autoCalcSd = Number(createForm.sd) || 0;
  const calcUCL = autoCalcMean + 3 * autoCalcSd;
  const calcLCL = autoCalcMean - 3 * autoCalcSd;
  const calcUWL = autoCalcMean + 2 * autoCalcSd;
  const calcLWL = autoCalcMean - 2 * autoCalcSd;

  const createMutation = useMutation({
    mutationFn: (payload: typeof createForm & { ucl: number; lcl: number; uwl: number; lwl: number }) =>
      api.post('/qc-charts', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc-charts'] });
      setShowCreate(false);
      setCreateForm({ parameter: '', unit: '', labId: '', mean: '', sd: '', instrument: '', method: '' });
    },
  });

  const addPointMutation = useMutation({
    mutationFn: (payload: typeof pointForm) =>
      api.post(`/qc-charts/${selectedChart!.id}/datapoints`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc-charts'] });
      queryClient.invalidateQueries({ queryKey: ['qc-chart-detail'] });
      setShowAddPoint(false);
      setPointForm({ value: '', runDate: '', analyst: '', runId: '', notes: '' });
    },
  });

  // Build violations list
  const violations = activeDataPoints.filter((dp) => !!dp.violation);

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'chartCode', label: 'Chart Code', width: 'w-32',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.chartCode)}</span>,
    },
    {
      key: 'parameter', label: 'Parameter / Unit',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.parameter)}</p>
          <p className="text-xs text-text-dim">{String(row.unit)}</p>
        </div>
      ),
    },
    {
      key: 'lab', label: 'Lab / Instrument',
      render: (row) => (
        <div>
          <p className="text-xs text-text">{String(row.lab)}</p>
          <p className="text-xs text-text-dim">{row.instrument ? String(row.instrument) : '—'}</p>
        </div>
      ),
    },
    {
      key: 'mean', label: 'Mean ± SD',
      render: (row) => (
        <span className="text-xs font-mono text-text">
          {(Number(row.mean) || 0).toFixed(2)} ± {(Number(row.sd) || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'lastRunDate', label: 'Last Run',
      render: (row) => (
        <span className="text-xs font-mono text-text-dim">
          {row.lastRunDate ? format(new Date(String(row.lastRunDate)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
    {
      key: 'recentViolation', label: 'Violations',
      render: (row) => (
        row.recentViolation ? (
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
            {String(row.recentViolation).split(' — ')[0]}
          </span>
        ) : (
          <span className="text-xs text-green-600 font-medium">None</span>
        )
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="QC Control Charts"
        subtitle="Levey-Jennings statistical process control"
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={13} /> New Chart
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Active Charts" value={stats.active} icon={BarChart2} color="#b07d1a" />
        <StatCard label="Violations Today" value={stats.violationsToday} icon={AlertTriangle} color="#DC2626" highlight />
        <StatCard label="Total Data Points" value={stats.totalPoints} icon={Database} color="#2563EB" />
        <StatCard label="Charts Needing Attention" value={stats.needAttention} icon={Eye} color="#D97706" highlight />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by parameter, chart code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <select
          value={labFilter}
          onChange={(e) => { setLabFilter(e.target.value); setPage(1); }}
          className="select w-36"
        >
          <option value="">All Labs</option>
          <option value="qc1">QC Lab 1</option>
          <option value="qc2">QC Lab 2</option>
          <option value="micro">Microbiology</option>
          <option value="analytical">Analytical</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedChart(row as unknown as QCChart)}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Chart Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedChart}
        onClose={() => setSelectedChart(null)}
        title={selectedChart?.chartCode || ''}
        subtitle={`${selectedChart?.parameter} · ${selectedChart?.lab}`}
        width={680}
        footer={
          <button onClick={() => setShowAddPoint(true)} className="btn-primary">
            <Plus size={13} /> Add Data Point
          </button>
        }
      >
        {selectedChart && (
          <div className="space-y-5">
            {/* Chart Metadata */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Mean', value: (Number(selectedChart.mean) || 0).toFixed(3) },
                { label: 'SD', value: (Number(selectedChart.sd) || 0).toFixed(3) },
                { label: 'UCL (+3SD)', value: (Number(selectedChart.ucl) || 0).toFixed(3) },
                { label: 'UWL (+2SD)', value: (Number(selectedChart.uwl) || 0).toFixed(3) },
                { label: 'LWL (−2SD)', value: (Number(selectedChart.lwl) || 0).toFixed(3) },
                { label: 'LCL (−3SD)', value: (Number(selectedChart.lcl) || 0).toFixed(3) },
              ].map(({ label, value }) => (
                <div key={label} className="p-2.5 bg-bg rounded-lg text-center">
                  <p className="text-[10px] uppercase font-semibold text-text-dim">{label}</p>
                  <p className="text-sm font-bold font-mono text-text mt-0.5">{value}</p>
                </div>
              ))}
            </div>

            {/* Instrument / Method */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Instrument</p>
                <p className="text-xs text-text font-medium">{selectedChart.instrument || '—'}</p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Method</p>
                <p className="text-xs text-text font-medium">{selectedChart.method || '—'}</p>
              </div>
            </div>

            {/* Levey-Jennings Chart */}
            {activeDataPoints.length === 0 ? (
              <EmptyState icon="data" title="No data points" description="Add data points to render the Levey-Jennings chart." />
            ) : (
              <ChartCard
                title="Levey-Jennings Chart"
                subtitle={`${selectedChart.parameter} (${selectedChart.unit})`}
                height={280}
              >
                <ResponsiveContainer>
                  <LineChart
                    data={activeDataPoints.map((dp, idx) => ({
                      ...dp,
                      runLabel: dp.runId || `Run ${idx + 1}`,
                    }))}
                    margin={{ top: 10, right: 20, bottom: 10, left: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.4} />
                    <XAxis
                      dataKey="runLabel"
                      tick={{ fontSize: 9, fill: '#6899A0' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#6899A0' }}
                      width={45}
                      domain={[
                        Math.min(selectedChart.lcl, ...activeDataPoints.map((d) => Number(d.value))) - selectedChart.sd,
                        Math.max(selectedChart.ucl, ...activeDataPoints.map((d) => Number(d.value))) + selectedChart.sd,
                      ]}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 8 }}
                      formatter={(value: number, name: string) => [`${(Number(value) || 0).toFixed(3)} ${selectedChart.unit}`, name]}
                      labelFormatter={(label) => `Run: ${label}`}
                    />

                    {/* Control Limit Reference Lines */}
                    <ReferenceLine y={selectedChart.ucl} stroke="#DC2626" strokeDasharray="5 3" strokeWidth={1.5}
                      label={{ value: 'UCL', position: 'right', fontSize: 9, fill: '#DC2626', fontWeight: 600 }} />
                    <ReferenceLine y={selectedChart.uwl} stroke="#D97706" strokeDasharray="4 3" strokeWidth={1}
                      label={{ value: 'UWL', position: 'right', fontSize: 9, fill: '#D97706' }} />
                    <ReferenceLine y={selectedChart.mean} stroke="#16A34A" strokeWidth={1.5}
                      label={{ value: `Mean (${(Number(selectedChart.mean) || 0).toFixed(2)})`, position: 'right', fontSize: 9, fill: '#16A34A', fontWeight: 600 }} />
                    <ReferenceLine y={selectedChart.lwl} stroke="#D97706" strokeDasharray="4 3" strokeWidth={1}
                      label={{ value: 'LWL', position: 'right', fontSize: 9, fill: '#D97706' }} />
                    <ReferenceLine y={selectedChart.lcl} stroke="#DC2626" strokeDasharray="5 3" strokeWidth={1.5}
                      label={{ value: 'LCL', position: 'right', fontSize: 9, fill: '#DC2626', fontWeight: 600 }} />

                    <Line
                      type="linear"
                      dataKey="value"
                      name={selectedChart.parameter}
                      stroke="#b07d1a"
                      strokeWidth={1.5}
                      dot={<LJDot />}
                      activeDot={{ r: 7, fill: '#b07d1a' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* Westgard Violations */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                <AlertTriangle size={12} className="text-red-500" /> Westgard Rule Violations ({violations.length})
              </p>
              {violations.length === 0 ? (
                <p className="text-xs text-green-600 font-medium py-2">No violations detected — process in control.</p>
              ) : (
                <div className="space-y-1.5">
                  {violations.map((dp) => (
                    <div key={dp.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-red-50 border border-red-100">
                      <AlertTriangle size={12} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-red-700">{dp.violation}</p>
                        <p className="text-[10px] text-red-500 mt-0.5">
                          Value: <span className="font-mono">{(Number(dp.value) || 0).toFixed(3)} {selectedChart.unit}</span>
                          {' · '}
                          {dp.runDate ? format(new Date(dp.runDate), 'd MMM yyyy HH:mm') : '—'}
                          {dp.analyst && ` · ${dp.analyst}`}
                        </p>
                      </div>
                      <span className="text-[10px] font-mono text-red-400 flex-shrink-0">{dp.runId}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Data Points Table */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1.5">
                <TrendingUp size={12} className="text-teal" /> Data Points ({activeDataPoints.length})
              </p>
              {activeDataPoints.length > 0 ? (
                <div className="rounded-lg border border-border overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-bg z-10">
                      <tr className="border-b border-border">
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Run ID</th>
                        <th className="text-right px-3 py-2 text-text-dim font-semibold">Value</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Date</th>
                        <th className="text-left px-3 py-2 text-text-dim font-semibold">Analyst</th>
                        <th className="text-center px-3 py-2 text-text-dim font-semibold">Flag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeDataPoints.map((dp, idx) => (
                        <tr key={dp.id} className={`${idx % 2 === 0 ? 'bg-card' : 'bg-bg'} ${dp.violation ? 'bg-red-50/50' : ''}`}>
                          <td className="px-3 py-1.5 font-mono text-text-muted">{dp.runId || '—'}</td>
                          <td className={`px-3 py-1.5 text-right font-mono font-semibold ${dp.violation ? 'text-red-600' : 'text-text'}`}>
                            {(Number(dp.value) || 0).toFixed(3)}
                          </td>
                          <td className="px-3 py-1.5 text-text-dim">
                            {dp.runDate ? format(new Date(dp.runDate), 'd MMM yyyy') : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-text-muted">{dp.analyst || '—'}</td>
                          <td className="px-3 py-1.5 text-center">
                            {dp.violation ? (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white">
                                {dp.violation.split(' — ')[0]}
                              </span>
                            ) : (
                              <span className="text-[10px] text-green-600">✓</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Create QC Chart Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New QC Control Chart"
        subtitle="Define a Levey-Jennings chart for a QC parameter"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() =>
                createMutation.mutate({
                  ...createForm,
                  ucl: calcUCL,
                  lcl: calcLCL,
                  uwl: calcUWL,
                  lwl: calcLWL,
                })
              }
              disabled={createMutation.isPending || !createForm.parameter || !createForm.labId || !createForm.mean || !createForm.sd}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Chart'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Parameter *</label>
            <input
              className="input"
              value={createForm.parameter}
              onChange={(e) => setCreateForm((f) => ({ ...f, parameter: e.target.value }))}
              placeholder="e.g. Assay, Dissolution"
            />
          </div>
          <div>
            <label className="label">Unit</label>
            <input
              className="input"
              value={createForm.unit}
              onChange={(e) => setCreateForm((f) => ({ ...f, unit: e.target.value }))}
              placeholder="e.g. %, mg/mL"
            />
          </div>
          <div>
            <label className="label">Lab *</label>
            <select
              className="select"
              value={createForm.labId}
              onChange={(e) => setCreateForm((f) => ({ ...f, labId: e.target.value }))}
            >
              <option value="">Select lab...</option>
              <option value="qc1">QC Lab 1</option>
              <option value="qc2">QC Lab 2</option>
              <option value="micro">Microbiology</option>
              <option value="analytical">Analytical Lab</option>
            </select>
          </div>
          <div>
            <label className="label">Instrument</label>
            <input
              className="input"
              value={createForm.instrument}
              onChange={(e) => setCreateForm((f) => ({ ...f, instrument: e.target.value }))}
              placeholder="e.g. HPLC-1, UV-Vis"
            />
          </div>
          <div>
            <label className="label">Mean *</label>
            <input
              type="number"
              step="any"
              className="input"
              value={createForm.mean}
              onChange={(e) => setCreateForm((f) => ({ ...f, mean: e.target.value }))}
              placeholder="Target mean value"
            />
          </div>
          <div>
            <label className="label">Standard Deviation (SD) *</label>
            <input
              type="number"
              step="any"
              className="input"
              value={createForm.sd}
              onChange={(e) => setCreateForm((f) => ({ ...f, sd: e.target.value }))}
              placeholder="Historical SD"
            />
          </div>
          <div>
            <label className="label">Method</label>
            <input
              className="input"
              value={createForm.method}
              onChange={(e) => setCreateForm((f) => ({ ...f, method: e.target.value }))}
              placeholder="e.g. USP, BP, In-house"
            />
          </div>

          {/* Auto-calculated limits preview */}
          {createForm.mean && createForm.sd && (
            <div className="col-span-2 p-3 bg-teal/5 border border-teal/20 rounded-lg">
              <p className="text-xs font-semibold text-teal mb-2">Auto-Calculated Control Limits</p>
              <div className="grid grid-cols-4 gap-2 text-xs">
                {[
                  { label: 'UCL (+3SD)', value: calcUCL, color: 'text-red-600' },
                  { label: 'UWL (+2SD)', value: calcUWL, color: 'text-amber-600' },
                  { label: 'LWL (−2SD)', value: calcLWL, color: 'text-amber-600' },
                  { label: 'LCL (−3SD)', value: calcLCL, color: 'text-red-600' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="text-center p-2 bg-bg rounded">
                    <p className="text-text-dim text-[10px]">{label}</p>
                    <p className={`font-mono font-bold ${color}`}>{(Number(value) || 0).toFixed(3)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Add Data Point Modal */}
      <Modal
        isOpen={showAddPoint}
        onClose={() => { setShowAddPoint(false); setPointForm({ value: '', runDate: '', analyst: '', runId: '', notes: '' }); }}
        title="Add Data Point"
        subtitle={selectedChart ? `Chart: ${selectedChart.chartCode} — ${selectedChart.parameter}` : ''}
        size="md"
        footer={
          <>
            <button onClick={() => { setShowAddPoint(false); setPointForm({ value: '', runDate: '', analyst: '', runId: '', notes: '' }); }} className="btn-secondary">Cancel</button>
            <button
              onClick={() => addPointMutation.mutate(pointForm)}
              disabled={addPointMutation.isPending || !pointForm.value || !pointForm.runDate}
              className="btn-primary"
            >
              {addPointMutation.isPending ? 'Saving...' : 'Save Data Point'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Value *</label>
            <input
              type="number"
              step="any"
              className="input"
              value={pointForm.value}
              onChange={(e) => setPointForm((f) => ({ ...f, value: e.target.value }))}
              placeholder={selectedChart ? `${selectedChart.unit}` : 'Measured value'}
            />
            {selectedChart && pointForm.value && (
              <p className={`text-[11px] mt-1 font-semibold ${
                Number(pointForm.value) > selectedChart.ucl || Number(pointForm.value) < selectedChart.lcl
                  ? 'text-red-600'
                  : Number(pointForm.value) > selectedChart.uwl || Number(pointForm.value) < selectedChart.lwl
                  ? 'text-amber-600'
                  : 'text-green-600'
              }`}>
                {Number(pointForm.value) > selectedChart.ucl || Number(pointForm.value) < selectedChart.lcl
                  ? 'Warning: Value outside 3SD control limits'
                  : Number(pointForm.value) > selectedChart.uwl || Number(pointForm.value) < selectedChart.lwl
                  ? 'Note: Value outside 2SD warning limits'
                  : 'Value within control limits'}
              </p>
            )}
          </div>
          <div>
            <label className="label">Run Date *</label>
            <input
              type="datetime-local"
              className="input"
              value={pointForm.runDate}
              onChange={(e) => setPointForm((f) => ({ ...f, runDate: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Analyst</label>
            <input
              className="input"
              value={pointForm.analyst}
              onChange={(e) => setPointForm((f) => ({ ...f, analyst: e.target.value }))}
              placeholder="Analyst name..."
            />
          </div>
          <div>
            <label className="label">Run ID</label>
            <input
              className="input"
              value={pointForm.runId}
              onChange={(e) => setPointForm((f) => ({ ...f, runId: e.target.value }))}
              placeholder="e.g. RUN-001"
            />
          </div>
          <div className="col-span-2">
            <label className="label">Notes</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={pointForm.notes}
              onChange={(e) => setPointForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Any notes about this run..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
